import { useCallback, useContext, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import ExportMenu from '../../Shared/ExportMenu'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import useDebounce from '../../../utils/useDebounce'
import {
  createMachineReceipt,
  deleteMachineReceipt,
  editMachineReceipt,
  getAllMachineReceipts,
} from '../../../services/machineRecipeServices'
import { mensagemDeErro } from '../../../services/apiClient'
import PermissionComponent, { usePode } from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'

const EMPTY_FORM = {
  subCompanyId: '',
  sequenceReceipt: '',
  description: '',
  internalPartNumber: '',
}

const EXPORT_COLUMNS = [
  { header: 'Sequência do Recibo', accessor: 'sequenceReceipt' },
  { header: 'Descrição', accessor: 'description' },
  { header: 'Número Interno da Peça', accessor: 'internalPartNumber' },
]

const MachineRecipe = () => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()
  const podeGerenciar = usePode(PERMISSOES.RECEITA_GERENCIAR)

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState('')
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const [itemToDelete, setItemToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useDebounce(
    () => {
      setAppliedSearch(search)
      setPage(0)
    },
    500,
    [search]
  )

  const fetchData = useCallback(async () => {
    if (!subCompanyId) {
      setRows([])
      setTotal(0)
      return
    }
    setLoading(true)
    try {
      const response = await getAllMachineReceipts({
        subCompanyId,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        search: appliedSearch,
      })
      setRows(response.data.result || [])
      setTotal(response.data.total || 0)
    } catch (error) {
      setRows([])
      setTotal(0)
      notify(mensagemDeErro(error, 'Não foi possível carregar as receitas de máquina.'), 'error')
    } finally {
      setLoading(false)
    }
  }, [subCompanyId, page, rowsPerPage, appliedSearch, notify])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Export leva todo o filtro, não só a página: pagina a API em lotes.
  const fetchAllForExport = useCallback(async () => {
    if (!subCompanyId) return []
    const BLOCO = 200
    const todas = []
    let offset = 0
    for (;;) {
      const response = await getAllMachineReceipts({
        subCompanyId,
        limit: BLOCO,
        offset,
        search: appliedSearch,
      })
      const itens = response.data.result || []
      todas.push(...itens)
      offset += BLOCO
      if (offset >= (response.data.total || 0) || itens.length === 0) break
    }
    return todas
  }, [subCompanyId, appliedSearch])

  const openCreate = () => {
    setEditingId('')
    setForm({ ...EMPTY_FORM, subCompanyId })
    setDialogOpen(true)
  }

  const openEdit = row => {
    setEditingId(row.id)
    setForm({
      subCompanyId: row.subCompanyId || subCompanyId || '',
      sequenceReceipt: row.sequenceReceipt || '',
      description: row.description || '',
      internalPartNumber: row.internalPartNumber || '',
    })
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditingId('')
    setForm(EMPTY_FORM)
  }

  const updateForm = field => event =>
    setForm(current => ({ ...current, [field]: event.target.value }))

  const isFormValid =
    Boolean(form.subCompanyId) &&
    Boolean(form.sequenceReceipt.trim()) &&
    Boolean(form.description.trim()) &&
    Boolean(form.internalPartNumber.trim())

  const handleSave = async () => {
    if (!isFormValid) {
      notify('Preencha todos os campos obrigatórios.', 'warning')
      return
    }
    setSaving(true)
    try {
      const payload = {
        subCompanyId: form.subCompanyId,
        sequenceReceipt: form.sequenceReceipt.trim(),
        description: form.description.trim(),
        internalPartNumber: form.internalPartNumber.trim(),
      }
      if (editingId) {
        await editMachineReceipt({ ...payload, receiptMachineId: editingId })
        notify('Receita atualizada com sucesso!', 'success')
      } else {
        await createMachineReceipt(payload)
        notify('Receita criada com sucesso!', 'success')
      }
      closeDialog()
      fetchData()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar a receita de máquina.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!itemToDelete) return
    setDeleting(true)
    try {
      await deleteMachineReceipt({ receiptMachineId: itemToDelete.id })
      notify('Receita excluída com sucesso!', 'success')
      setItemToDelete(null)
      fetchData()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível excluir a receita de máquina.'), 'error')
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    {
      id: 'actions',
      label: 'Ações',
      width: 110,
      element: row => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={podeGerenciar ? 'Editar' : 'Sem permissão para editar receitas'}>
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={!podeGerenciar}
                onClick={() => openEdit(row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={podeGerenciar ? 'Excluir' : 'Sem permissão para excluir receitas'}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={!podeGerenciar}
                onClick={() => setItemToDelete(row)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'sequenceReceipt',
      label: 'Sequência do Recibo',
      element: row => row.sequenceReceipt || '—',
    },
    { id: 'description', label: 'Descrição', element: row => row.description || '—' },
    {
      id: 'internalPartNumber',
      label: 'Número Interno da Peça',
      element: row => row.internalPartNumber || '—',
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Receitas de Máquina"
        actions={
          <>
            <ExportMenu
              rows={rows}
              resolveRows={fetchAllForExport}
              columns={EXPORT_COLUMNS}
              fileName="receitas-de-maquina"
              title="Receitas de Máquina"
              disabled={total === 0}
            />
            <PermissionComponent permissao={PERMISSOES.RECEITA_GERENCIAR}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!subCompanyId}
                onClick={openCreate}
              >
                Criar
              </Button>
            </PermissionComponent>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => {
                setSubCompanyId(event.target.value)
                setPage(0)
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Procurar por receita"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {!subCompanyId ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Selecione uma filial para listar as receitas de máquina.
          </Typography>
        </Paper>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={event => {
            setRowsPerPage(parseInt(event.target.value, 10))
            setPage(0)
          }}
          rowKey={row => row.id}
          emptyMessage="Nenhuma receita de máquina encontrada."
        />
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Editar receita de máquina' : 'Criar receita de máquina'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="machine-recipe-subcompany-label">Filial para cadastro</InputLabel>
                <Select
                  labelId="machine-recipe-subcompany-label"
                  label="Filial para cadastro"
                  value={form.subCompanyId}
                  onChange={updateForm('subCompanyId')}
                  MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                >
                  {(subCompany || []).map(item => (
                    <MenuItem key={item.subCompanyId} value={item.subCompanyId}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Sequência do Recibo"
                value={form.sequenceReceipt}
                onChange={updateForm('sequenceReceipt')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Descrição"
                value={form.description}
                onChange={updateForm('description')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Número Interno da Peça"
                value={form.internalPartNumber}
                onChange={updateForm('internalPartNumber')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} color="inherit">
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !isFormValid}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(itemToDelete)}
        title="Excluir receita de máquina"
        description="Tem certeza que deseja excluir esse item?"
        confirmLabel="Excluir"
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setItemToDelete(null)}
      />

      <FeedbackSnackbar />
    </Box>
  )
}

export default MachineRecipe
